import {
  getGmailConnectionStatusAPI,
  initiateGmailConnectAPI,
  disconnectGmailAPI,
  scanGmailInboxAPI,
  scanGmailInboxStatusAPI,
  listStagedITBsAPI,
  importITBsAPI,
  importITBsStatusAPI,
  rejectITBAPI,
} from "../../../utils/requests/deal";
import {
  GMAIL_CONNECTION_STATUS_REQUEST,
  GMAIL_CONNECTION_STATUS_SUCCESS,
  GMAIL_CONNECTION_STATUS_FAIL,
  GMAIL_CONNECT_REQUEST,
  GMAIL_CONNECT_SUCCESS,
  GMAIL_CONNECT_FAIL,
  GMAIL_DISCONNECT_REQUEST,
  GMAIL_DISCONNECT_SUCCESS,
  GMAIL_DISCONNECT_FAIL,
  SCAN_GMAIL_INBOX_REQUEST,
  SCAN_GMAIL_INBOX_SUCCESS,
  SCAN_GMAIL_INBOX_FAIL,
  SCAN_GMAIL_INBOX_STATUS_UPDATE,
  LIST_STAGED_ITBS_REQUEST,
  LIST_STAGED_ITBS_SUCCESS,
  LIST_STAGED_ITBS_FAIL,
  IMPORT_ITBS_REQUEST,
  IMPORT_ITBS_SUCCESS,
  IMPORT_ITBS_FAIL,
  REJECT_ITB_REQUEST,
  REJECT_ITB_SUCCESS,
  REJECT_ITB_FAIL,
  ITB_SELECT,
  ITB_SELECT_ALL,
  ITB_DESELECT_ALL,
  ITB_CLEAR_SELECTION,
  ITB_SET_FILTER,
  ITB_SET_SUCCESS_MESSAGE,
  ITB_SET_ERROR_MESSAGE,
  ITB_CLEAR_MESSAGES,
  IMPORT_ITBS_STATUS_UPDATE,
} from "../../constants/itbConstants";

export const getGmailConnectionStatus = () => async (dispatch, getState) => {
  try {
    dispatch({ type: GMAIL_CONNECTION_STATUS_REQUEST });
    const response = await getGmailConnectionStatusAPI();
    if (response) {
      dispatch({
        type: GMAIL_CONNECTION_STATUS_SUCCESS,
        payload: {
          is_connected: response.is_connected || false,
          connected_email: response.connected_email || null,
          connected_at: response.connected_at || null,
        },
      });
    } else {
      dispatch({
        type: GMAIL_CONNECTION_STATUS_SUCCESS,
        payload: {
          is_connected: false,
          connected_email: null,
          connected_at: null,
        },
      });
    }
  } catch (error) {
    console.error("Error fetching Gmail status:", error);
    dispatch({
      type: GMAIL_CONNECTION_STATUS_FAIL,
      payload: error,
    });
    dispatch({
      type: GMAIL_CONNECTION_STATUS_SUCCESS,
      payload: {
        is_connected: false,
        connected_email: null,
        connected_at: null,
      },
    });
  }
};

// Initiate Gmail Connection
export const connectGmail = () => async (dispatch, getState) => {
  try {
    dispatch({ type: GMAIL_CONNECT_REQUEST });
    const response = await initiateGmailConnectAPI();
    if (response && response.authorization_url) {
      dispatch({ type: GMAIL_CONNECT_SUCCESS });
      // Redirect to Google OAuth
      window.location.href = response.authorization_url;
    } else {
      dispatch({
        type: GMAIL_CONNECT_FAIL,
        payload: "Failed to get authorization URL",
      });
      dispatch({
        type: ITB_SET_ERROR_MESSAGE,
        payload: "Failed to get authorization URL. Please try again.",
      });
    }
  } catch (error) {
    console.error("Error connecting Gmail:", error);
    dispatch({
      type: GMAIL_CONNECT_FAIL,
      payload: error,
    });
    dispatch({
      type: ITB_SET_ERROR_MESSAGE,
      payload: "Failed to initiate Gmail connection. Please try again.",
    });
  }
};

// Disconnect Gmail
export const disconnectGmail = () => async (dispatch, getState) => {
  try {
    dispatch({ type: GMAIL_DISCONNECT_REQUEST });
    const response = await disconnectGmailAPI();
    if (response) {
      dispatch({
        type: GMAIL_DISCONNECT_SUCCESS,
        payload: {
          is_connected: false,
          connected_email: null,
          connected_at: null,
        },
      });
      dispatch({
        type: LIST_STAGED_ITBS_SUCCESS,
        payload: { staged_itbs: [], counts: { pending: 0, imported: 0, rejected: 0 } },
      });
      dispatch({
        type: ITB_SET_SUCCESS_MESSAGE,
        payload: "Gmail disconnected successfully",
      });
    }
  } catch (error) {
    console.error("Error disconnecting Gmail:", error);
    dispatch({
      type: GMAIL_DISCONNECT_FAIL,
      payload: error,
    });
    dispatch({
      type: ITB_SET_ERROR_MESSAGE,
      payload: "Failed to disconnect Gmail",
    });
  }
};

// Scan Gmail Inbox - Now async with polling
export const scanGmailInbox = (maxResults = 50) => async (dispatch, getState) => {
  try {
    dispatch({ type: SCAN_GMAIL_INBOX_REQUEST });
    dispatch({ type: ITB_CLEAR_MESSAGES });
    
    // Start the scan task
    const response = await scanGmailInboxAPI({ max_results: maxResults });
    
    if (response && response.success) {
      // Start polling for status
      dispatch(pollScanStatus());
    } else {
      dispatch({
        type: SCAN_GMAIL_INBOX_FAIL,
        payload: "Invalid response",
      });
      dispatch({
        type: ITB_SET_ERROR_MESSAGE,
        payload: response?.message || "Failed to start scan",
      });
    }
  } catch (error) {
    console.error("Error starting scan:", error);
    const errorData = error.response?.data;
    dispatch({
      type: SCAN_GMAIL_INBOX_FAIL,
      payload: error,
    });

    // Check if reconnection is needed
    if (errorData?.needs_reconnect) {
      let message = errorData.message || "Gmail needs to be reconnected";
      if (errorData.instructions && Array.isArray(errorData.instructions)) {
        message += "\n\n" + errorData.instructions.join("\n");
      }
      if (errorData.revoke_url) {
        message += `\n\nDirect link: ${errorData.revoke_url}`;
      }
      dispatch({
        type: ITB_SET_ERROR_MESSAGE,
        payload: message,
      });
      // Refresh status after a delay
      setTimeout(() => {
        dispatch(getGmailConnectionStatus());
      }, 1500);
    } else {
      dispatch({
        type: ITB_SET_ERROR_MESSAGE,
        payload: errorData?.message || error.message || "Failed to start scan",
      });
    }
  }
};

// Poll scan status
export const pollScanStatus = () => async (dispatch, getState) => {
  const pollInterval = 5000; // Poll every 5 seconds
  const maxPolls = 300; // Max 25 minutes (300 * 5s)
  let pollCount = 0;

  const poll = async () => {
    try {
      const statusResponse = await scanGmailInboxStatusAPI();
      
      if (statusResponse) {
        dispatch({
          type: SCAN_GMAIL_INBOX_STATUS_UPDATE,
          payload: statusResponse,
        });

        const status = statusResponse.status;

        if (status === 'completed') {
          // Scan completed successfully
          dispatch({
            type: SCAN_GMAIL_INBOX_SUCCESS,
            payload: { itbs_found: statusResponse.itbs_found || 0 },
          });
          
          // Refresh staged ITBs
          const { itb } = getState();
          dispatch(listStagedITBs(itb.filter || "pending"));
          
          dispatch({
            type: ITB_SET_SUCCESS_MESSAGE,
            payload:
              statusResponse.itbs_found > 0
                ? `Found ${statusResponse.itbs_found} ITBs in your inbox`
                : "No ITBs found in your inbox",
          });
          
          // Clear status after showing message
          setTimeout(() => {
            dispatch({
              type: SCAN_GMAIL_INBOX_STATUS_UPDATE,
              payload: null,
            });
          }, 5000);
          
          return; // Stop polling
        } else if (status === 'error') {
          // Scan failed
          dispatch({
            type: SCAN_GMAIL_INBOX_FAIL,
            payload: statusResponse,
          });
          
          dispatch({
            type: ITB_SET_ERROR_MESSAGE,
            payload: statusResponse.message || "Scan failed",
          });
          
          // Clear status
          setTimeout(() => {
            dispatch({
              type: SCAN_GMAIL_INBOX_STATUS_UPDATE,
              payload: null,
            });
          }, 5000);
          
          return; // Stop polling
        } else if (status === 'processing' || status === 'queued') {
          // Continue polling
          pollCount++;
          if (pollCount < maxPolls) {
            setTimeout(poll, pollInterval);
          } else {
            // Timeout
            dispatch({
              type: SCAN_GMAIL_INBOX_FAIL,
              payload: "Scan timeout",
            });
            dispatch({
              type: ITB_SET_ERROR_MESSAGE,
              payload: "Scan is taking too long. Please try again.",
            });
          }
        }
      } else {
        // Status not found - might be completed or expired
        pollCount++;
        if (pollCount < 5) {
          // Try a few more times in case of temporary cache miss
          setTimeout(poll, pollInterval);
        } else {
          dispatch({
            type: SCAN_GMAIL_INBOX_FAIL,
            payload: "Status not found",
          });
        }
      }
    } catch (error) {
      console.error("Error polling scan status:", error);
      pollCount++;
      if (pollCount < maxPolls) {
        setTimeout(poll, pollInterval);
      } else {
        dispatch({
          type: SCAN_GMAIL_INBOX_FAIL,
          payload: error,
        });
      }
    }
  };

  // Start polling
  setTimeout(poll, pollInterval);
};

// Check scan status on page load
export const checkScanStatus = () => async (dispatch, getState) => {
  try {
    const statusResponse = await scanGmailInboxStatusAPI();
    
    if (statusResponse) {
      dispatch({
        type: SCAN_GMAIL_INBOX_STATUS_UPDATE,
        payload: statusResponse,
      });

      const status = statusResponse.status;

      // If scan is in progress, resume polling
      if (status === 'processing' || status === 'queued') {
        dispatch(pollScanStatus());
      } else if (status === 'completed') {
        // Scan was completed, show results
        dispatch({
          type: SCAN_GMAIL_INBOX_SUCCESS,
          payload: { itbs_found: statusResponse.itbs_found || 0 },
        });
        
        // Refresh staged ITBs
        const { itb } = getState();
        dispatch(listStagedITBs(itb.filter || "pending"));
        
        // Clear status after a delay
        setTimeout(() => {
          dispatch({
            type: SCAN_GMAIL_INBOX_STATUS_UPDATE,
            payload: null,
          });
        }, 5000);
      } else if (status === 'error') {
        // Scan had an error
        dispatch({
          type: SCAN_GMAIL_INBOX_FAIL,
          payload: statusResponse,
        });
        
        // Clear status after a delay
        setTimeout(() => {
          dispatch({
            type: SCAN_GMAIL_INBOX_STATUS_UPDATE,
            payload: null,
          });
        }, 5000);
      }
    }
  } catch (error) {
    // Status not found or error - that's okay, just means no active scan
    console.log("No active scan found:", error);
  }
};

// List Staged ITBs
export const listStagedITBs = (status = "pending") => async (dispatch, getState) => {
  try {
    dispatch({ type: LIST_STAGED_ITBS_REQUEST });
    const response = await listStagedITBsAPI({ status });
    if (response) {
      dispatch({
        type: LIST_STAGED_ITBS_SUCCESS,
        payload: {
          staged_itbs: response.staged_itbs || [],
          counts: response.counts || { pending: 0, imported: 0, rejected: 0 },
        },
      });
    } else {
      dispatch({
        type: LIST_STAGED_ITBS_SUCCESS,
        payload: {
          staged_itbs: [],
          counts: { pending: 0, imported: 0, rejected: 0 },
        },
      });
    }
  } catch (error) {
    console.error("Error fetching staged ITBs:", error);
    dispatch({
      type: LIST_STAGED_ITBS_FAIL,
      payload: error,
    });
    dispatch({
      type: LIST_STAGED_ITBS_SUCCESS,
      payload: {
        staged_itbs: [],
        counts: { pending: 0, imported: 0, rejected: 0 },
      },
    });
  }
};

// Import ITBs - Now async with polling
export const importITBs = (stagedItbIds) => async (dispatch, getState) => {
  if (!stagedItbIds || stagedItbIds.length === 0) {
    dispatch({
      type: ITB_SET_ERROR_MESSAGE,
      payload: "Please select at least one ITB to import",
    });
    return;
  }

  try {
    dispatch({ type: IMPORT_ITBS_REQUEST });
    dispatch({ type: ITB_CLEAR_MESSAGES });
    
    // Start the import task
    const response = await importITBsAPI({ staged_itb_ids: stagedItbIds });
    
    if (response && response.success) {
      // Start polling for status
      dispatch(pollImportStatus());
    } else {
      dispatch({
        type: IMPORT_ITBS_FAIL,
        payload: "Invalid response",
      });
      dispatch({
        type: ITB_SET_ERROR_MESSAGE,
        payload: response?.message || "Failed to start import",
      });
    }
  } catch (error) {
    console.error("Error starting import:", error);
    const errorData = error.response?.data;
    dispatch({
      type: IMPORT_ITBS_FAIL,
      payload: error,
    });
    dispatch({
      type: ITB_SET_ERROR_MESSAGE,
      payload: errorData?.message || error.message || "Failed to start import",
    });
  }
};

// Poll import status
export const pollImportStatus = () => async (dispatch, getState) => {
  const pollInterval = 2000; // Poll every 2 seconds (faster than scan since imports are usually quicker)
  const maxPolls = 600; // Max 20 minutes (600 * 2s) for large imports
  let pollCount = 0;

  const poll = async () => {
    try {
      const statusResponse = await importITBsStatusAPI();
      
      if (statusResponse) {
        dispatch({
          type: IMPORT_ITBS_STATUS_UPDATE,
          payload: statusResponse,
        });

        const status = statusResponse.status;

        if (status === 'completed') {
          // Import completed successfully
          dispatch({
            type: IMPORT_ITBS_SUCCESS,
            payload: { 
              imported_count: statusResponse.imported_count || 0,
              errors: statusResponse.errors || []
            },
          });
          dispatch({
            type: ITB_CLEAR_SELECTION,
          });
          
          const errorCount = (statusResponse.errors || []).length;
          const successCount = statusResponse.imported_count || 0;
          
          if (errorCount > 0) {
            dispatch({
              type: ITB_SET_SUCCESS_MESSAGE,
              payload: `Successfully imported ${successCount} deals. ${errorCount} failed.`,
            });
          } else {
            dispatch({
              type: ITB_SET_SUCCESS_MESSAGE,
              payload: `Successfully imported ${successCount} deals!`,
            });
          }
          
          // Refresh list
          const { itb } = getState();
          dispatch(listStagedITBs(itb.filter || "pending"));
          
          // Clear status after showing message
          setTimeout(() => {
            dispatch({
              type: IMPORT_ITBS_STATUS_UPDATE,
              payload: null,
            });
          }, 5000);
          
          return; // Stop polling
        } else if (status === 'error') {
          // Import failed
          dispatch({
            type: IMPORT_ITBS_FAIL,
            payload: statusResponse,
          });
          
          dispatch({
            type: ITB_SET_ERROR_MESSAGE,
            payload: statusResponse.message || "Import failed",
          });
          
          // Clear status
          setTimeout(() => {
            dispatch({
              type: IMPORT_ITBS_STATUS_UPDATE,
              payload: null,
            });
          }, 5000);
          
          return; // Stop polling
        } else if (status === 'processing' || status === 'queued') {
          // Continue polling
          pollCount++;
          if (pollCount < maxPolls) {
            setTimeout(poll, pollInterval);
          } else {
            // Timeout
            dispatch({
              type: IMPORT_ITBS_FAIL,
              payload: "Import timeout",
            });
            dispatch({
              type: ITB_SET_ERROR_MESSAGE,
              payload: "Import is taking too long. Please check back later or try again.",
            });
          }
        }
      } else {
        // Status not found - might be completed or expired
        pollCount++;
        if (pollCount < 5) {
          // Try a few more times in case of temporary cache miss
          setTimeout(poll, pollInterval);
        } else {
          dispatch({
            type: IMPORT_ITBS_FAIL,
            payload: "Status not found",
          });
        }
      }
    } catch (error) {
      console.error("Error polling import status:", error);
      pollCount++;
      if (pollCount < maxPolls) {
        setTimeout(poll, pollInterval);
      } else {
        dispatch({
          type: IMPORT_ITBS_FAIL,
          payload: error,
        });
      }
    }
  };

  // Start polling
  setTimeout(poll, pollInterval);
};

// Check import status on page load
export const checkImportStatus = () => async (dispatch, getState) => {
  try {
    const statusResponse = await importITBsStatusAPI();
    
    if (statusResponse) {
      dispatch({
        type: IMPORT_ITBS_STATUS_UPDATE,
        payload: statusResponse,
      });

      const status = statusResponse.status;

      // If import is in progress, resume polling
      if (status === 'processing' || status === 'queued') {
        dispatch(pollImportStatus());
      } else if (status === 'completed') {
        // Import was completed, show results
        dispatch({
          type: IMPORT_ITBS_SUCCESS,
          payload: { 
            imported_count: statusResponse.imported_count || 0,
            errors: statusResponse.errors || []
          },
        });
        
        // Refresh staged ITBs
        const { itb } = getState();
        dispatch(listStagedITBs(itb.filter || "pending"));
        
        // Clear status after a delay
        setTimeout(() => {
          dispatch({
            type: IMPORT_ITBS_STATUS_UPDATE,
            payload: null,
          });
        }, 5000);
      } else if (status === 'error') {
        // Import had an error
        dispatch({
          type: IMPORT_ITBS_FAIL,
          payload: statusResponse,
        });
        
        // Clear status after a delay
        setTimeout(() => {
          dispatch({
            type: IMPORT_ITBS_STATUS_UPDATE,
            payload: null,
          });
        }, 5000);
      }
    }
  } catch (error) {
    // Status not found or error - that's okay, just means no active import
    console.log("No active import found:", error);
  }
};

// Reject ITB
export const rejectITB = (itbId) => async (dispatch, getState) => {
  try {
    dispatch({ type: REJECT_ITB_REQUEST });
    const response = await rejectITBAPI(itbId);
    if (response) {
      dispatch({
        type: REJECT_ITB_SUCCESS,
        payload: itbId,
      });
      dispatch({
        type: ITB_SET_SUCCESS_MESSAGE,
        payload: "ITB rejected successfully",
      });
      // Refresh list
      const { itb } = getState();
      dispatch(listStagedITBs(itb.filter || "pending"));
    }
  } catch (error) {
    console.error("Error rejecting ITB:", error);
    dispatch({
      type: REJECT_ITB_FAIL,
      payload: error,
    });
    dispatch({
      type: ITB_SET_ERROR_MESSAGE,
      payload: "Failed to reject ITB",
    });
  }
};

// Selection Actions
export const selectITB = (itbId) => ({
  type: ITB_SELECT,
  payload: itbId,
});

export const selectAllITBs = () => ({
  type: ITB_SELECT_ALL,
});

export const deselectAllITBs = () => ({
  type: ITB_DESELECT_ALL,
});

export const clearITBSelection = () => ({
  type: ITB_CLEAR_SELECTION,
});

// Filter Action
export const setITBFilter = (filter) => ({
  type: ITB_SET_FILTER,
  payload: filter,
});

// Message Actions
export const setITBSuccessMessage = (message) => ({
  type: ITB_SET_SUCCESS_MESSAGE,
  payload: message,
});

export const setITBErrorMessage = (message) => ({
  type: ITB_SET_ERROR_MESSAGE,
  payload: message,
});

export const clearITBMessages = () => ({
  type: ITB_CLEAR_MESSAGES,
});

